import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { inr, pct, xx, type Deal } from '@krit/core';
import { listIntake, patchDeal, type DealDto } from '../lib/api.js';
import { Card, StatTile, PageHeader, Pill, Skeleton } from '../components/ui.js';

const FIT_TONE: Record<string, string> = { Fit: 'green', Borderline: 'amber', Pass: 'red' };

function Metric({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-[0.4px] text-muted-2">{label}</div>
      <div className={`text-[13px] font-bold mono mt-0.5 ${tone ?? 'text-navy'}`}>{value}</div>
    </div>
  );
}

export function Intake() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['intake'], queryFn: listIntake, retry: 2, retryDelay: 800 });
  const items = data?.deals ?? [];

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Deal['status'] }) => patchDeal(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['intake'] }); qc.invalidateQueries({ queryKey: ['deals'] }); },
  });

  const fit = items.filter((d) => d.computed.thesisFit === 'Fit').length;
  const borderline = items.filter((d) => d.computed.thesisFit === 'Borderline').length;

  return (
    <div>
      <PageHeader eyebrow="Sourcing" title="WhatsApp Intake" desc="Decks forwarded to the intake number, auto-summarised and thesis-scored. Accept into the pipeline or dismiss." />

      <div className="grid grid-cols-3 gap-3 mb-5 max-w-[520px]">
        <StatTile label="In intake" value={items.length} note="awaiting review" accent="gold" />
        <StatTile label="Fit thesis" value={fit} note="pass hard filters" accent="green" />
        <StatTile label="Borderline" value={borderline} note="need a look" accent="amber" />
      </div>

      {isLoading ? (
        <div role="status" aria-busy="true" aria-label="Loading intake" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {Array.from({ length: 3 }).map((_, i) => (
            <section key={i} className="bg-card border border-line rounded-xl shadow-sh">
              <div className="px-5 py-3.5 border-b border-line-2"><Skeleton w={160} h={14} className="mb-1.5" /><Skeleton w={100} h={10} /></div>
              <div className="p-5"><Skeleton lines={3} h={11} className="mb-3" /><div className="grid grid-cols-4 gap-2">{Array.from({ length: 4 }).map((_, j) => <Skeleton key={j} h={28} />)}</div></div>
            </section>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card><div className="py-12 text-center text-muted"><div className="text-[34px] opacity-30 mb-2">✷</div>Intake is empty. Forwarded decks will appear here.</div></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {items.map((d) => (
            <IntakeCard key={d.id} d={d}
              onOpen={() => navigate(`/deals/${d.id}`)}
              onAccept={() => move.mutate({ id: d.id, status: 'Under Review' })}
              onDismiss={() => move.mutate({ id: d.id, status: 'Not Interested' })}
              busy={move.isPending} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntakeCard({ d, onOpen, onAccept, onDismiss, busy }: { d: DealDto; onOpen: () => void; onAccept: () => void; onDismiss: () => void; busy: boolean }) {
  const c = d.computed;
  const mosTone = c.mos == null ? 'text-muted-2' : c.mos >= 30 ? 'text-green' : c.mos >= 15 ? 'text-amber' : 'text-red';
  return (
    <div className="flex flex-col bg-card border border-line rounded-xl shadow-sh overflow-hidden transition-shadow hover:shadow-sh2">
      <div className="h-1 bg-gradient-to-r from-teal to-teal-d" />
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-gold flex items-center gap-1">✷ {d.banker?.replace(/^WhatsApp · /, '') || 'WhatsApp'}</div>
            <button onClick={onOpen} className="block text-left font-extrabold text-navy text-[14.5px] leading-tight mt-1 hover:text-teal-d truncate w-full">{d.company}</button>
            <div className="text-[11px] text-muted mt-0.5">{d.sector} · {d.location}</div>
          </div>
          <Pill tone={FIT_TONE[c.thesisFit]!}>{c.thesisFit}</Pill>
        </div>

        <div className="grid grid-cols-5 gap-1.5 mt-3.5 py-3 border-y border-line-2">
          <Metric label="Rev26" value={inr(d.rev26)} />
          <Metric label="PAT26" value={inr(d.pat26)} />
          <Metric label="Ask" value={inr(d.valuationAsk)} />
          <Metric label="Impl P/E" value={c.entryPE == null ? '—' : xx(c.entryPE)} />
          <Metric label="MoS" value={c.mos == null ? '—' : pct(c.mos)} tone={mosTone} />
        </div>

        {d.business && <p className="text-[12px] text-muted leading-relaxed mt-3 line-clamp-3">{d.business}</p>}

        <div className="flex items-center gap-2 mt-auto pt-4">
          <button disabled={busy} onClick={onAccept} className="flex-1 px-3 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">✓ Accept to pipeline</button>
          <button disabled={busy} onClick={onDismiss} className="px-3 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-muted border border-line hover:bg-canvas disabled:opacity-50">Dismiss</button>
        </div>
      </div>
    </div>
  );
}
